import { useState, useEffect } from 'react'
import { AdminLayout } from '../../components/layouts/Layout'
import { DataTable } from '../../components/common/DataTable'
import { Card, Button, Badge, Modal, Textarea } from '../../components/common/UI'
import { Building, CheckCircle, XCircle, Ban, PlayCircle, Eye } from 'lucide-react'
import { formatters } from '../../utils/formatting'
import { businessService, merchantService } from '../../services/api'
import type { Business } from '../../types'

export default function Businesses() {
  const [businesses, setBusinesses] = useState<Business[]>([])
  const [merchants, setMerchants] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [showVerificationModal, setShowVerificationModal] = useState(false)
  const [verificationAction, setVerificationAction] = useState<'VERIFY' | 'REJECT'>('VERIFY')
  const [rejectionReason, setRejectionReason] = useState('')
  const [filterStatus, setFilterStatus] = useState<'ALL' | 'ACTIVE' | 'SUSPENDED' | 'INACTIVE'>('ALL')
  const [filterVerification, setFilterVerification] = useState<'ALL' | 'PENDING' | 'VERIFIED' | 'REJECTED'>('ALL')

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [businessesData, merchantsData] = await Promise.all([
        businessService.list(),
        merchantService.list()
      ])
      setBusinesses(businessesData)
      setMerchants(merchantsData)
    } catch (error) {
      console.error('Failed to load businesses:', error)
    } finally {
      setLoading(false)
    }
  }

  const getMerchant = (merchantId: string) => {
    return merchants.find(m => m.id === merchantId)
  }

  const handleVerify = async () => {
    if (!selectedBusiness) return

    try {
      await businessService.update(selectedBusiness.id, {
        verificationStatus: 'VERIFIED'
      })
      setShowVerificationModal(false)
      setSelectedBusiness(null)
      await loadData()
    } catch (error) {
      console.error('Failed to verify business:', error)
    }
  }

  const handleReject = async () => {
    if (!selectedBusiness || !rejectionReason.trim()) return

    try {
      await businessService.update(selectedBusiness.id, {
        verificationStatus: 'REJECTED'
      })
      setShowVerificationModal(false)
      setSelectedBusiness(null)
      setRejectionReason('')
      await loadData()
    } catch (error) {
      console.error('Failed to reject business:', error)
    }
  }

  const handleStatusChange = async (business: Business, newStatus: 'ACTIVE' | 'SUSPENDED' | 'INACTIVE') => {
    try {
      await businessService.update(business.id, {
        status: newStatus
      })
      await loadData()
    } catch (error) {
      console.error('Failed to update business status:', error)
    }
  }

  const openVerificationModal = (business: Business, action: 'VERIFY' | 'REJECT') => {
    setSelectedBusiness(business)
    setVerificationAction(action)
    setShowVerificationModal(true)
  }

  const getFilteredBusinesses = () => {
    return businesses.filter(business => {
      const statusMatch = filterStatus === 'ALL' || business.status === filterStatus
      const verificationMatch = filterVerification === 'ALL' || business.verificationStatus === filterVerification
      return statusMatch && verificationMatch
    })
  }

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'success'
      case 'SUSPENDED':
        return 'warning'
      case 'INACTIVE':
        return 'default'
      default:
        return 'default'
    }
  }

  const getVerificationBadgeVariant = (status: string) => {
    switch (status) {
      case 'VERIFIED':
        return 'success'
      case 'PENDING':
        return 'warning'
      case 'REJECTED':
        return 'error'
      default:
        return 'default'
    }
  }

  const columns = [
    {
      key: 'businessName' as const,
      label: 'Business Name',
      render: (value: string, row: Business) => (
        <div className="flex items-center space-x-3">
          {row.logo ? (
            <img src={row.logo} alt={value} className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
              <Building className="w-5 h-5 text-gray-500" />
            </div>
          )}
          <div>
            <p className="font-medium">{value}</p>
            <p className="text-xs text-gray-500">{row.businessType || 'N/A'}</p>
          </div>
        </div>
      )
    },
    {
      key: 'merchantId' as const,
      label: 'Merchant',
      render: (value: string) => {
        const merchant = getMerchant(value)
        return merchant ? (
          <div>
            <p className="text-sm">{merchant.fullName}</p>
            <p className="text-xs text-gray-500">{merchant.email}</p>
          </div>
        ) : (
          <span className="text-gray-400">Unknown</span>
        )
      }
    },
    {
      key: 'businessEmail' as const,
      label: 'Contact',
      render: (value: string, row: Business) => (
        <div>
          <p className="text-sm">{value}</p>
          <p className="text-xs text-gray-500">{row.phoneNumber || 'N/A'}</p>
        </div>
      )
    },
    {
      key: 'verificationStatus' as const,
      label: 'Verification',
      render: (value: string) => (
        <Badge variant={getVerificationBadgeVariant(value)}>{value}</Badge>
      )
    },
    {
      key: 'status' as const,
      label: 'Status',
      render: (value: string) => (
        <Badge variant={getStatusBadgeVariant(value)}>{value}</Badge>
      )
    },
    {
      key: 'createdAt' as const,
      label: 'Created',
      render: (value: string) => formatters.date(value)
    }
  ]

  const actions = [
    {
      icon: Eye,
      label: 'View Details',
      onClick: (row: Business) => {
        setSelectedBusiness(row)
        setShowDetailsModal(true)
      }
    },
    {
      icon: CheckCircle,
      label: 'Verify',
      onClick: (row: Business) => openVerificationModal(row, 'VERIFY'),
      hidden: (row: Business) => row.verificationStatus === 'VERIFIED'
    },
    {
      icon: XCircle,
      label: 'Reject',
      onClick: (row: Business) => openVerificationModal(row, 'REJECT'),
      variant: 'ghost' as const,
      hidden: (row: Business) => row.verificationStatus === 'REJECTED'
    },
    {
      icon: Ban,
      label: 'Suspend',
      onClick: (row: Business) => handleStatusChange(row, 'SUSPENDED'),
      variant: 'ghost' as const,
      hidden: (row: Business) => row.status === 'SUSPENDED'
    },
    {
      icon: PlayCircle,
      label: 'Activate',
      onClick: (row: Business) => handleStatusChange(row, 'ACTIVE'),
      variant: 'ghost' as const,
      hidden: (row: Business) => row.status === 'ACTIVE'
    }
  ]

  const filteredBusinesses = getFilteredBusinesses()

  const stats = {
    total: businesses.length,
    verified: businesses.filter(b => b.verificationStatus === 'VERIFIED').length,
    pending: businesses.filter(b => b.verificationStatus === 'PENDING').length,
    active: businesses.filter(b => b.status === 'ACTIVE').length
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full"></div>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Business Management</h1>
            <p className="text-gray-600">Manage and verify business accounts</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Businesses</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Building className="w-8 h-8 text-blue-600" />
            </div>
          </Card>
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Verified</p>
                <p className="text-2xl font-bold">{stats.verified}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </Card>
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Pending</p>
                <p className="text-2xl font-bold">{stats.pending}</p>
              </div>
              <XCircle className="w-8 h-8 text-orange-600" />
            </div>
          </Card>
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Active</p>
                <p className="text-2xl font-bold">{stats.active}</p>
              </div>
              <PlayCircle className="w-8 h-8 text-purple-600" />
            </div>
          </Card>
        </div>

        <Card>
          <div className="flex flex-wrap gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2">Status Filter</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
                className="border border-gray-300 rounded-lg px-4 py-2"
              >
                <option value="ALL">All Status</option>
                <option value="ACTIVE">Active</option>
                <option value="SUSPENDED">Suspended</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Verification Filter</label>
              <select
                value={filterVerification}
                onChange={(e) => setFilterVerification(e.target.value as any)}
                className="border border-gray-300 rounded-lg px-4 py-2"
              >
                <option value="ALL">All Verification</option>
                <option value="PENDING">Pending</option>
                <option value="VERIFIED">Verified</option>
                <option value="REJECTED">Rejected</option>
              </select>
            </div>
          </div>

          <DataTable 
            data={filteredBusinesses} 
            columns={columns} 
            actions={actions}
          />
        </Card>

        <Modal
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false)
            setSelectedBusiness(null)
          }}
          title="Business Details"
        >
          {selectedBusiness && (
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                {selectedBusiness.logo ? (
                  <img 
                    src={selectedBusiness.logo} 
                    alt={selectedBusiness.businessName}
                    className="w-20 h-20 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center">
                    <Building className="w-10 h-10 text-gray-500" />
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-semibold">{selectedBusiness.businessName}</h3>
                  <p className="text-sm text-gray-600">{selectedBusiness.businessType || 'N/A'}</p>
                </div>
              </div>

              {selectedBusiness.banner && (
                <div>
                  <p className="text-sm font-medium mb-2">Banner</p>
                  <img 
                    src={selectedBusiness.banner} 
                    alt="Banner"
                    className="w-full h-32 object-cover rounded-lg"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Contact Person</p>
                  <p className="font-medium">{selectedBusiness.contactPerson}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Business Email</p>
                  <p className="font-medium">{selectedBusiness.businessEmail}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <p className="font-medium">{selectedBusiness.phoneNumber || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Website</p>
                  <p className="font-medium">{selectedBusiness.website || 'N/A'}</p>
                </div>
              </div>

              {selectedBusiness.location?.address && (
                <div>
                  <p className="text-sm text-gray-600">Address</p>
                  <p className="font-medium">{selectedBusiness.location.address}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Verification Status</p>
                  <Badge variant={getVerificationBadgeVariant(selectedBusiness.verificationStatus)}>
                    {selectedBusiness.verificationStatus}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Account Status</p>
                  <Badge variant={getStatusBadgeVariant(selectedBusiness.status)}>
                    {selectedBusiness.status}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Created</p>
                  <p className="font-medium">{formatters.date(selectedBusiness.createdAt)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Updated</p>
                  <p className="font-medium">{formatters.date(selectedBusiness.updatedAt)}</p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                {selectedBusiness.verificationStatus !== 'VERIFIED' && (
                  <Button
                    onClick={() => {
                      setShowDetailsModal(false)
                      openVerificationModal(selectedBusiness, 'VERIFY')
                    }}
                    icon={CheckCircle}
                  >
                    Verify Business
                  </Button>
                )}
                {selectedBusiness.verificationStatus !== 'REJECTED' && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowDetailsModal(false)
                      openVerificationModal(selectedBusiness, 'REJECT')
                    }}
                    icon={XCircle}
                  >
                    Reject Business
                  </Button>
                )}
              </div>
            </div>
          )}
        </Modal>

        <Modal
          isOpen={showVerificationModal}
          onClose={() => {
            setShowVerificationModal(false)
            setSelectedBusiness(null)
            setRejectionReason('')
          }}
          title={verificationAction === 'VERIFY' ? 'Verify Business' : 'Reject Business'}
        >
          {selectedBusiness && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">{selectedBusiness.businessName}</h3>
                <p className="text-sm text-gray-600">
                  {verificationAction === 'VERIFY' 
                    ? 'Are you sure you want to verify this business? This will allow them to run advertisements.'
                    : 'Please provide a reason for rejecting this business verification.'
                  }
                </p>
              </div>

              {verificationAction === 'REJECT' && (
                <Textarea
                  label="Rejection Reason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={4}
                  placeholder="Provide a detailed reason for rejection..."
                  required
                />
              )}

              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowVerificationModal(false)
                    setSelectedBusiness(null)
                    setRejectionReason('')
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={verificationAction === 'VERIFY' ? handleVerify : handleReject}
                  variant={verificationAction === 'VERIFY' ? 'primary' : 'danger'}
                  disabled={verificationAction === 'REJECT' && !rejectionReason.trim()}
                >
                  {verificationAction === 'VERIFY' ? 'Verify Business' : 'Reject Business'}
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </AdminLayout>
  )
}